<script lang="ts">
  import WrapField from "./WrapField.svelte";
  import Composer from "$components/editor/Composer.svelte";
  import cx from "classnames";
  import { getContext, onMount } from "svelte";
  import { twMerge } from "tailwind-merge";
  import type { createFormStore } from "../store";
  import type { YdocStore } from "$components/editor/ydoc";
  import type { User } from "$stores/user";

  export let ydoc: YdocStore | null;
  export let user: User;
  export let name: string;
  export let label: string = "";
  export let help: string = "";
  export let embedsBlocks = false;
  export let inlineMentions = false;
  export let validationRules: Record<string, any> = {};
  export let placeholder: string = "Add thread body...";
  export let advancedBlocks: boolean = false;
  export let wrapperClassname = "";
  export let enableToolMentions: boolean = false;
  export let inputClassname = "";
  export let variant: "primary" | "secondary" = "primary";

  const formStore: ReturnType<typeof createFormStore> | undefined = getContext("form");
  let ref: HTMLDivElement;
  let value = $formStore?.values[name] || "";
  // need initial value set once, separate from form state to initialise Editor
  let content = value?.json;

  const variants = {
    // MAIN VARIANTS
    primary: cx(
      "bg-transparent px-0 py-2 border-0", // light mode styles
      "dark:bg-transparent dark:text-neutral-300" // dark mode styles
    ),

    secondary: cx(
      "rounded-[10px] bg-transparent border-[0.5px] border-black/20 p-6 focus-within:border-black/40 focus-within:outline-none focus-within:ring-0", // light mode styles
      "dark:bg-transparent dark:text-neutral-100 dark:border-white/10 dark:focus-within:border-white/30" // dark mode styles
    ),
  };

  function onUpdate(update: any) {
    if (formStore) {
      formStore.setField(name, {
        json: update.json,
        text: update.text,
        empty: update.empty,
        markdown: update.markdown,
      });
    }
  }

  function onSubmit() {
    if (formStore) {
      $formStore?.submitForm();
    }
  }

  onMount(() => {
    if (formStore) {
      formStore.addRef(name, ref);
    }
  });
</script>

<WrapField {label} {help} {name} {wrapperClassname} {validationRules}>
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    bind:this={ref}
    class={twMerge(variants[variant], "h-full rounded-[10px] transition-colors", inputClassname)}
    on:keydown={(k) => {
      if (k.key === "Tab") {
        // Without this, since this is a form field, the browser will switch focus to the next form field.
        // Disabling this allows the use of Tab key for special actions in the text editor (indenting bullet points).
        // Just noting here that this is potentially problematic for people using screen readers who want to tab to
        // the next field.
        k.preventDefault();
        k.stopPropagation();
      }
    }}
  >
    <Composer
      editable
      {ydoc}
      {content}
      {placeholder}
      {advancedBlocks}
      on:update={(e) => onUpdate(e.detail)}
      on:submit={onSubmit}
      highlightColor=""
      {user}
      {enableToolMentions}
      {embedsBlocks}
      {inlineMentions}
    />
  </div>
</WrapField>
